import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../../ui/common/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/common/select";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { resolveShowingRange, resolveTotalPages } from "../helpers/versionHistory.helpers";

interface VersionHistoryPaginationBarProps {
  pageNumber: number;
  pageSize: number;
  total: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const VersionHistoryPaginationBar = ({
  pageNumber,
  pageSize,
  total,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange
}: VersionHistoryPaginationBarProps) => {
  const totalPages = resolveTotalPages(total, pageSize);
  const { from, to } = resolveShowingRange(pageNumber, pageSize, total);

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-t tw-border-border tw-flex-wrap">
      <div className="tw-flex tw-items-center tw-gap-2">
        <Select value={String(pageSize)} onValueChange={value => onPageSizeChange(Number(value))}>
          <SelectTrigger size="sm" className="tw-w-[72px] tw-text-xs" aria-label="Versions per page">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map(size => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="tw-text-sm tw-font-mono tw-text-muted-foreground">
          {VERSION_HISTORY_COPY.showingRange(from, to, total)}
        </p>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="tw-h-8 tw-w-8"
          disabled={pageNumber <= 1}
          onClick={() => onPageChange(pageNumber - 1)}
        >
          <ChevronLeft className="tw-h-4 tw-w-4" />
        </Button>
        <span className="tw-text-sm tw-font-mono tw-text-muted-foreground tw-px-1">
          {pageNumber} / {Math.max(totalPages, 1)}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="tw-h-8 tw-w-8"
          disabled={pageNumber >= totalPages}
          onClick={() => onPageChange(pageNumber + 1)}
        >
          <ChevronRight className="tw-h-4 tw-w-4" />
        </Button>
      </div>
    </div>
  );
};
